import React, { useState, useRef } from "react";
import { X, ImagePlus, Loader2 } from "lucide-react";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db } from "../../firebase";
import { useAppContext } from "../../context/AppContext";

export default function BugReportModal({
  setShowBugModal,
}: {
  setShowBugModal: (v: boolean) => void;
}) {
  const { lang, user, activePet } = useAppContext();
  const [bugText, setBugText] = useState("");

  // Стан для завантаження фото
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Перевірка на розмір файлу (макс 10MB)
    if (file.size > 10 * 1024 * 1024) {
      return alert(
        lang === "uk"
          ? "Файл занадто великий (макс. 10MB)"
          : "File too large (max 10MB)"
      );
    }

    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleSendBug = async () => {
    // Відправляємо, якщо є хоча б текст АБО фото
    if ((!bugText.trim() && !imageFile) || !user) return;

    setIsUploading(true);
    let imageUrl = null;

    try {
      // 1. Якщо є фото, спочатку завантажуємо його в Storage
      if (imageFile) {
        const storage = getStorage();
        const fileExt = imageFile.name.split(".").pop();
        const fileName = `feedback_media/${Date.now()}_${user.uid}.${fileExt}`;
        const storageRef = ref(storage, fileName);

        await uploadBytes(storageRef, imageFile);
        imageUrl = await getDownloadURL(storageRef);
      }

      // 2. Зберігаємо дані в Firestore
      await addDoc(collection(db, "feedback"), {
        text: bugText,
        mediaUrl: imageUrl,
        authorId: user.uid,
        authorName: activePet?.name || "Користувач",
        createdAt: serverTimestamp(),
      });

      setBugText("");
      setImageFile(null);
      setImagePreview(null);
      setShowBugModal(false);
      alert(
        lang === "uk" ? "Дякуємо! Відгук надіслано." : "Feedback submitted."
      );
    } catch (error) {
      console.error(error);
      alert(
        lang === "uk"
          ? "Помилка при відправці. Спробуйте ще раз."
          : "Error sending feedback."
      );
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 z-[600] flex flex-col justify-end backdrop-blur-sm animate-fade-in"
      onClick={() => setShowBugModal(false)}
    >
      <div
        className="bg-[var(--bg-card)] rounded-t-[2.5rem] p-6 pb-[100px] animate-slide-up border-t border-[var(--border-color)] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-6">
          <h3 className="font-extrabold text-xl text-[var(--text-main)]">
            Зворотний зв'язок
          </h3>
          <button
            onClick={() => setShowBugModal(false)}
            className="text-[var(--text-main)] active:scale-95 transition-all"
            disabled={isUploading}
          >
            <X size={24} />
          </button>
        </div>

        <textarea
          value={bugText}
          onChange={(e) => setBugText(e.target.value)}
          placeholder="Опишіть проблему або вашу ідею..."
          className="w-full p-4 bg-[var(--bg-soft)] border border-[var(--border-color)] text-[var(--text-main)] rounded-xl outline-none text-sm min-h-[120px] mb-3 resize-none"
          disabled={isUploading}
        />

        {/* Прев'ю вибраного зображення */}
        {imagePreview && (
          <div className="relative mb-4 rounded-xl overflow-hidden border border-[var(--border-color)] bg-[var(--bg-soft)] flex justify-center items-center h-[120px]">
            <img
              src={imagePreview}
              alt="Preview"
              className="max-h-full object-contain"
            />
            <button
              onClick={() => {
                setImageFile(null);
                setImagePreview(null);
              }}
              className="absolute top-2 right-2 bg-black/60 p-1.5 rounded-full text-white active:scale-95 transition-all"
              disabled={isUploading}
            >
              <X size={14} />
            </button>
          </div>
        )}

        <div className="flex gap-3 mt-auto">
          {/* Кнопка вибору зображення */}
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
            className="p-4 bg-[var(--bg-soft)] border border-[var(--border-color)] text-[var(--text-main)] rounded-xl active:scale-95 transition-all flex items-center justify-center disabled:opacity-50"
          >
            <ImagePlus size={24} />
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleImageChange}
          />

          {/* Кнопка відправки */}
          <button
            onClick={handleSendBug}
            disabled={isUploading || (!bugText.trim() && !imageFile)}
            className="flex-1 flex items-center justify-center gap-2 py-4 bg-[#a073e7] text-white font-bold rounded-xl active:scale-95 transition-all shadow-md disabled:opacity-50"
          >
            {isUploading ? (
              <>
                <Loader2 size={20} className="animate-spin" />
                Відправка...
              </>
            ) : (
              "Відправити розробникам"
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
